import OpenAI from 'openai';
import StreamProcessor from './StreamProcessor';
import { ApiType, EventType, } from './types';
import { PATH_PREFIX } from '@config/constant';
import { getModuleLogger } from '@utils/logger';
const logger = getModuleLogger('chat-sdk');
class ChatSDK {
    client;
    constructor(options = {}) {
        const proto = location.protocol.slice(0, -1); // 去掉末尾的 ':'
        const dns = `${location.host}${PATH_PREFIX}`;
        const baseURL = `${proto}://${dns}/api/xy/${options.isRuntime ? 'runtime' : 'design'}/llm`;
        logger.info('[ChatSDK] 初始化', { isRuntime: options.isRuntime, baseURL });
        this.client = new OpenAI({
            baseURL,
            apiKey: '',
            dangerouslyAllowBrowser: true,
            defaultHeaders: {
                'x-stainless-arch': null,
                'x-stainless-lang': null,
                'x-stainless-os': null,
                'x-stainless-package-version': null,
                'x-stainless-retry-count': null,
                'x-stainless-runtime': null,
                'x-stainless-runtime-version': null,
                'x-stainless-timeout': null,
            },
            // 自定义 fetch 以传递 credentials: 'include'（用于 cookie 认证）
            fetch: (url, init) => fetch(url, Object.assign({}, init, { credentials: 'include' })),
        });
    }
    /**
     * 发送单条聊天消息，后台存储上下文
     */
    async send(message, options = {}) {
        logger.info('[ChatSDK.send] 发送单条消息', { sessionId: message.sessionId, messageLength: message.message.length });
        return this.doRequest(message, ApiType.Line, options);
    }
    /**
     * 发送全部聊天记录，后台不会拼接上下文
     */
    async sendAll(messages, options = {}) {
        logger.info('[ChatSDK.sendAll] 发送批量消息', {
            sessionId: messages.sessionId,
            messageCount: messages.messages?.length || 0,
        });
        return this.doRequest(messages, ApiType.Batch, options);
    }
    /**
     * 将消息转换为 OpenAI 格式并提取模型配置
     */
    convertToOpenAIFormat(message, apiType) {
        let messages = [];
        let modelConfig = {};
        if (apiType === ApiType.Line) {
            const requestMessage = message;
            if (requestMessage.systemContent) {
                messages.push({ role: 'system', content: requestMessage.systemContent });
            }
            messages.push({ role: 'user', content: requestMessage.message });
            modelConfig = requestMessage.modelConfig || {};
        }
        else {
            const batchMessage = message;
            messages = batchMessage.messages || [];
            modelConfig = batchMessage.modelConfig || {};
        }
        const { model = '', ...extraConfig } = modelConfig;
        logger.debug('[ChatSDK.convertToOpenAIFormat] 消息转换完成', {
            apiType,
            messageCount: messages.length,
            model,
            hasExtraConfig: Object.keys(extraConfig).length > 0,
        });
        return {
            messages: messages,
            model,
            extraConfig,
        };
    }
    /**
     * 使用 OpenAI SDK 发起请求并返回 StreamProcessor
     */
    async doRequest(messages, apiType, options = {}) {
        const abortController = new AbortController();
        const streamProcessor = new StreamProcessor(abortController);
        const { query = {}, headers = {} } = options;
        // 转换为 OpenAI 格式
        const { messages: openAIMessages, model, extraConfig } = this.convertToOpenAIFormat(messages, apiType);
        // 合并 query 参数（projectId, pageId 等）
        const queryParams = {
            ...query,
            ...this.getBrowserContext(),
        };
        logger.info('[ChatSDK.doRequest] 开始发起请求', {
            apiType,
            model,
            messageCount: openAIMessages.length,
            queryParams,
        });
        try {
            // 使用 OpenAI SDK 发起流式请求
            const stream = await this.client.chat.completions.create({
                messages: openAIMessages,
                model, // 后端会处理默认值
                stream: true,
                ...extraConfig,
            }, {
                query: queryParams,
                headers: {
                    ...headers,
                },
                signal: abortController.signal,
            });
            logger.debug('[ChatSDK.doRequest] 流式请求已创建，开始处理流');
            // 异步处理流
            streamProcessor.processStream(stream).catch((e) => {
                if (!abortController.signal.aborted) {
                    const err = e instanceof Error ? e : new Error(String(e));
                    logger.error('[ChatSDK.doRequest] 流处理错误', err);
                    streamProcessor.emitEvent(EventType.Error, err);
                }
            });
        }
        catch (e) {
            if (!abortController.signal.aborted) {
                const err = e instanceof Error ? e : new Error(String(e));
                logger.error('[ChatSDK.doRequest] 请求失败', err, { apiType, model });
                // 延迟发出错误事件，确保调用者有时间设置错误监听器
                setTimeout(() => {
                    streamProcessor.emitEvent(EventType.Error, err);
                }, 0);
            }
            else {
                logger.debug('[ChatSDK.doRequest] 请求已取消');
            }
        }
        return streamProcessor;
    }
    /**
     * 获取浏览器环境上下文信息
     */
    getBrowserContext() {
        if (typeof window === 'undefined') {
            logger.debug('[ChatSDK.getBrowserContext] 非浏览器环境，返回空对象');
            return {};
        }
        const urlParams = new URLSearchParams(window.location.search);
        // 支持大小写不敏感的参数名
        const projectId = urlParams.get('projectid') || urlParams.get('projectId');
        const pageId = urlParams.get('pageid') || urlParams.get('pageId');
        const context = {
            ...(projectId && { projectId }),
            ...(pageId && { pageId }),
        };
        logger.debug('[ChatSDK.getBrowserContext] 获取浏览器上下文', context);
        return context;
    }
}
export default ChatSDK;
export { EventType };
